import React, { useEffect, useState } from "react"
import { Card, Col, Row } from "reactstrap"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import Flatpickr from "react-flatpickr"
import { X } from "lucide-react"
import { QrReader } from "react-qr-reader"
import "flatpickr/dist/themes/material_blue.css"

import { markMultiplePracticalTrainingParticipantAttendance } from "store/actions/practicalTrainingAction"
import { showAlert } from "store/reducers/alertReducer"
import { formatDateForAPI } from "components/Common/Utils"
import CustomLabel from "components/Common/CustomLabel"

const MarkAttendance = ({ selectedUuid, selectedItem, toggle }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isLoading } = useSelector(state => state.TheoreticalTraining)

  const [national_ids, setNational_ids] = useState("")
  const [attendance_date, setAttendance_date] = useState(new Date())
  const [userType, setUserType] = useState(null)
  const [attendanceType, setAttendanceType] = useState("MANUAL")
  const [idEntities, setIdEntities] = useState([])
  const [totalAttended, setTotalAttended] = useState(
    selectedItem?.attendance_count || 0
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const storedUserType = localStorage.getItem("userType") || ""
    setUserType(storedUserType)
  }, [])

  const handleMarkAttendance = chunked => {
    const nationalIds = chunked ? chunked : idEntities
    const payload = {
      training_uuid: selectedUuid,
      national_ids: nationalIds,
      ...(userType === "superadmin" && {
        attendance_date: formatDateForAPI(attendance_date),
      }),
    }
    setIsSubmitting(true)
    dispatch(markMultiplePracticalTrainingParticipantAttendance(payload))
      .then(action => {
        const { statusCode, success, message } = action?.payload || {}
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setTotalAttended(totalAttended + nationalIds.length)
          setNational_ids("")
          setIdEntities([])
          setIsSubmitting(false)
        } else {
          setNational_ids("")
          setIdEntities([])
          setIsSubmitting(false)
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(() => {})
  }

  const removeId = idToRemove => {
    const updatedEntities = idEntities.filter(id => id !== idToRemove)
    setIdEntities(updatedEntities)
    setNational_ids(updatedEntities.join(""))
  }

  const handleInputChange = e => {
    const input = e.target.value.replace(/[^0-9]/g, "")
    setNational_ids(input)

    // if (attendanceType === "MANUAL") {
    const chunked = input.match(/\d{10}/g) || []
    if (chunked.length > 0) {
      setIdEntities(prev => Array.from(new Set([...prev, ...chunked])))
      setNational_ids("")

      // if (attendanceType !== "MANUAL") {
      handleMarkAttendance(chunked)
      // }
    }

    // }
  }

  const handleQRScan = (result, error) => {
    if (result) {
      const matchedIds = result.getText().match(/\d{10}/g)
      handleInputChange({ target: { value: matchedIds?.join("") } })
    }
  }

  return (
    <div>
      <Row className="mb-3">
        <Col md={6} sm={12}>
          <label htmlFor="uploadType">{t("Attendance Type")}</label>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <input
                type="radio"
                name="uploadType"
                value="MANUAL"
                checked={attendanceType === "MANUAL"}
                onClick={() => setAttendanceType("MANUAL")}
                style={{ height: "unset" }}
                className="px-2"
              />
              <label className="px-1 mb-0">{t("Manual")}</label>
            </div>
            <div className="d-flex align-items-center">
              <input
                type="radio"
                name="uploadType"
                value="QR"
                checked={attendanceType === "QR"}
                onClick={() => {
                  setAttendanceType("QR")
                  setIdEntities([])
                  setNational_ids("")
                }}
                style={{ height: "unset" }}
                className="px-2"
              />
              <label className="px-1 mb-0">{t("By QR code")}</label>
            </div>
          </div>
        </Col>
        {userType === "superadmin" && (
          <Col md={6} sm={12}>
            <div className="mb-3 w-50">
              <Flatpickr
                value={attendance_date}
                onChange={selectedDates => setAttendance_date(selectedDates[0])}
                options={{ dateFormat: "Y-m-d" }}
                placeholder={t("Select Attendance Date")}
                className="form-control"
              />
            </div>
          </Col>
        )}
      </Row>
      <Row className="mb-4">
        <Card>
          <Row className="border rounded-3 p-4">
            <Col lg={6}>
              <h4 className="text-center">{t("Total Participants")}</h4>
              <h3 className="text-center">
                {selectedItem?.participants_count}
              </h3>
            </Col>
            <Col lg={6}>
              <h4 className="text-center">{t("Total Attended")}</h4>
              <h3 className="text-center">{totalAttended}</h3>
            </Col>
          </Row>
        </Card>

        <div className="row mb-3">
          <div className="col-lg-6"></div>
        </div>

        <div className="border rounded p-3">
          {attendanceType === "MANUAL" && idEntities.length > 0 && (
            <div className="mb-3 d-flex flex-wrap gap-2">
              {idEntities.map((id, index) => (
                <span
                  key={index}
                  className="badge bg-primary d-flex align-items-center gap-2"
                  style={{ fontSize: "14px", padding: "6px 10px" }}
                >
                  {id}
                  <X
                    size={16}
                    style={{ cursor: "pointer" }}
                    onClick={() => removeId(id)}
                  />
                </span>
              ))}
            </div>
          )}

          {attendanceType === "QR" && (
            <Col xs="12" className="mb-3">
              <CustomLabel label={t("Scan QR Code")} />
              <div className="border rounded overflow-hidden p-0">
                <QrReader
                  constraints={{ facingMode: { ideal: "environment" } }}
                  onResult={handleQRScan}
                />
              </div>
            </Col>
          )}

          {attendanceType === "MANUAL" && (
            <div className="form_field d-flex gap-2 my-3">
              <textarea
                name="national_ids"
                className="form-control"
                placeholder={t(
                  "Please enter national Id/ Iqama Id: eg. 1234657890,1234567890"
                )}
                value={national_ids}
                onChange={handleInputChange}
              ></textarea>
            </div>
          )}

          <div className="g_end mt-2 gap-3 cis_btn">
            <button
              className="main_btn disabled-button fit_content px-4"
              type="button"
              onClick={toggle}
              disabled={isLoading}
            >
              {t("Cancel")}
            </button>
            {attendanceType !== "QR" && (
              <button
                className="main_btn fit_content px-4"
                type="submit"
                disabled={idEntities.length === 0 || isSubmitting}
                onClick={() => handleMarkAttendance()}
              >
                {t("Submit")}
              </button>
            )}
          </div>
        </div>
      </Row>
    </div>
  )
}

export default MarkAttendance
